import { useState } from 'react'
import { Alert, Anchor, Badge, Button, Group, Modal, Paper, Stack, Text } from '@mantine/core'
import { IconInfoCircle, IconPlus } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  KNOB_HSMS, adicionarHsm, erroDaGestao, lerAjustes, type ClusterCloudHsm,
} from '@/api/cloudhsm'
import { DecisaoDoPedido } from '@/components/chamados/DecisaoDoPedido'
import { CHAVE_PEDIDO_TERRAFORM, useTerraformChangeRequest } from '@/hooks/useTerraformChangeRequest'
import { LinhaDoTempoDoPedido } from '@/pages/chat/components/LinhaDoTempoDoPedido'

/**
 * "Adicionar HSM" (consoleai#26388): o cluster nasce com 1 HSM, e o segundo (ou terceiro) entra
 * por um pedido do runner de Terraform — plan, aprovação, MR e apply, como qualquer ajuste.
 *
 * Quem diz se o botão serve é o backend (`GET .../ajustes`): stage fora do formato do molde,
 * cluster fora de ACTIVE ou um HSM por subnet já viram a FRASE na tela, e o botão não some sem
 * explicação. Antes de confirmar, a tela mostra o custo que o HSM acrescenta (a tabela do
 * `pricing.py`, a mesma do resumo do stage).
 */

/** Horas por mês da tabela de preço (a ressalva de `pricing.py` usa 730 h). */
export const HORAS_POR_MES = 730

export const CHAVE_AJUSTES = 'cloudhsm-ajustes'

const dolar = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function frasesDoCusto(custoHora: number | null): string {
  if (custoHora == null) return 'O custo desta região não está na tabela do ConsoleAI.'
  return `+US$ ${dolar(custoHora)}/h por HSM (≈ US$ ${dolar(custoHora * HORAS_POR_MES)}/mês, `
    + `${HORAS_POR_MES} h).`
}

export function AdicionarHsm({ tenantId, cluster, podeGerir }: {
  tenantId: number
  cluster: ClusterCloudHsm
  podeGerir: boolean
}) {
  const qc = useQueryClient()
  const chave = [CHAVE_AJUSTES, tenantId, cluster.cluster_id, cluster.regiao]
  const consulta = useQuery({
    queryKey: chave,
    queryFn: () => lerAjustes(tenantId, cluster.cluster_id, cluster.regiao),
    retry: false,
  })
  const [aberto, setAberto] = useState(false)
  const [recemCriado, setRecemCriado] = useState<number | null>(null)

  const pedir = useMutation({
    mutationFn: () => adicionarHsm(tenantId, cluster.cluster_id, cluster.regiao),
    onSuccess: (pedidoId) => {
      setAberto(false)
      setRecemCriado(pedidoId)
      void qc.invalidateQueries({ queryKey: chave })
    },
  })

  if (consulta.isError) {
    return (
      <Alert color="orange" icon={<IconInfoCircle size={16} />} title="Adicionar HSM">
        {erroDaGestao(consulta.error, 'Não deu para saber se este cluster pode ganhar um HSM.')}
      </Alert>
    )
  }
  const ajuste = consulta.data?.knobs[KNOB_HSMS]
  const ativo = consulta.data?.pedido_ativo
  const pedidoId = recemCriado ?? (ativo?.knob === KNOB_HSMS ? ativo.id : null)
  if (!ajuste && pedidoId == null) return null

  const de = typeof ajuste?.de?.hsms === 'number' ? ajuste.de.hsms : null
  const para = typeof ajuste?.para?.hsms === 'number' ? ajuste.para.hsms : null

  return (
    <Stack gap="xs" data-testid="adicionar-hsm">
      {ajuste && (ajuste.disponivel ? (
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm">
            {de != null && para != null ? `O cluster tem ${de} HSM; o pedido leva a ${para}. ` : ''}
            {frasesDoCusto(ajuste.custo_hora)}
          </Text>
          <Button size="xs" leftSection={<IconPlus size={14} />}
            disabled={!podeGerir || consulta.data?.pedido_ativo != null}
            title={!podeGerir ? 'Exige a permissão cloudhsm:manage' : undefined}
            onClick={() => { pedir.reset(); setAberto(true) }}>
            Adicionar HSM
          </Button>
        </Group>
      ) : (
        <Alert color="gray" icon={<IconInfoCircle size={16} />} title="Adicionar HSM indisponível">
          {ajuste.motivo}
        </Alert>
      ))}

      {pedidoId != null && <CartaoDoPedido pedidoId={pedidoId} />}

      <Modal opened={aberto} onClose={() => setAberto(false)} title="Adicionar HSM" centered>
        <Stack gap="sm">
          <Text size="sm">
            Abre um pedido no runner de Terraform que cria {para != null ? `o HSM ${para}` : 'um HSM'} no
            cluster {cluster.cluster_id}. Nada muda até o plan, a aprovação e o merge do MR.
          </Text>
          <Text size="sm" fw={600}>{frasesDoCusto(ajuste?.custo_hora ?? null)}</Text>
          {pedir.isError && (
            <Alert color="red">{erroDaGestao(pedir.error, 'O pedido não foi aberto.')}</Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAberto(false)}>Cancelar</Button>
            <Button loading={pedir.isPending} onClick={() => pedir.mutate()}>Abrir o pedido</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

/** O cartão do pedido do runner: estado, linha do tempo, MR e a decisão de quem aprova. */
export function CartaoDoPedido({ pedidoId }: { pedidoId: number }) {
  const qc = useQueryClient()
  const { data: pedido } = useTerraformChangeRequest(pedidoId)
  if (!pedido) {
    return <Text size="sm" c="dimmed">Pedido de Terraform #{pedidoId} aberto.</Text>
  }
  const rotulo = pedido.linha_do_tempo?.etapas?.find((e) => e.estado === pedido.estado)?.rotulo
    ?? pedido.estado
  const frase = pedido.linha_do_tempo?.em_curso?.frase ?? null
  return (
    <Paper withBorder p="sm" radius="md" data-testid="cartao-do-pedido">
      <Stack gap={6}>
        <Group gap="xs">
          <Text size="sm" fw={600}>Pedido de Terraform #{pedido.id}</Text>
          <Text size="xs" ff="monospace">{pedido.stage_key}</Text>
          <Badge size="xs" variant="light" color={pedido.terminal ? 'gray' : 'blue'}>{rotulo}</Badge>
        </Group>
        {frase && <Text size="sm">{frase}</Text>}
        {pedido.terminal && pedido.motivo_terminal && (
          <Text size="sm" c="dimmed">{pedido.motivo_terminal}</Text>
        )}
        <LinhaDoTempoDoPedido linha={pedido.linha_do_tempo} mrUrl={pedido.mr_url} />
        {pedido.mr_url && (
          <Anchor href={pedido.mr_url} target="_blank" rel="noreferrer" size="sm">
            Ver merge request
          </Anchor>
        )}
        <DecisaoDoPedido pedido={pedido}
          onDecidido={() => void qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM, pedidoId] })} />
      </Stack>
    </Paper>
  )
}
